import React, { useContext, useState } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { Table } from '@yuhuinc/ui-library';
import { basicSort } from '@yuhuinc/ui-library/components/Table/TableSorter';
import { colors } from 'ui-library/styles/colors';

import { FeesContext } from 'components/landlord_app/payments/LandlordFees';
import { Statuses } from './Form/constants';
import { AntdTag } from '@yuhuinc/ui-library';
import { Card } from 'antd';

const StyledClickToEditLink = styled.a`
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: ${colors.gray['9']};
`;

const FeesTable = ({
  onClickAddButton,
  renderRowActions,
  onEditClick,
  onResetClick,
  feeTemplateIncluded,
  statusFilters,
  isMobile,
}) => {
  const feesData = useContext(FeesContext);
  const { fees, loading } = feesData;

  const [filteredInfo, setFilteredInfo] = useState(null);
  const [sortedInfo, setSortedInfo] = useState(null);

  const renderClickToEdit = (text, rowData) => {
    return (
      <StyledClickToEditLink onClick={() => onEditClick(rowData.id)}>{text}</StyledClickToEditLink>
    );
  };

  const handleTableChange = (_, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleResetButtonClick = () => {
    setSortedInfo(null);
    setFilteredInfo(null);
    if (!!onResetClick) onResetClick();
  };

  return (
    <Card bordered>
      <Table
        actionButtonLabel="Add fee"
        actionButtonOnClick={onClickAddButton}
        showActionButton
        onChange={handleTableChange}
        dataSource={!!feeTemplateIncluded ? fees.filter((f) => feeTemplateIncluded(f)) : fees}
        loading={!!loading}
        pagination={{
          total: fees.length,
          hideOnSinglePage: true,
          size: 'small',
        }}
        resetButtonOnClick={handleResetButtonClick}
        title={null}
        scroll={{
          x: isMobile,
        }}
        columns={[
          {
            dataIndex: 'internal_name',
            dataType: 'text',
            key: 'internalName',
            ellipsis: true,
            title: 'Name',
            width: '40%',
            sortOrder: sortedInfo?.columnKey === 'internalName' && sortedInfo?.order,
            sorter: (a, b) => basicSort(a.internal_name, b.internal_name),
            render: (name, rowData) => renderClickToEdit(name, rowData),
          },
          {
            dataIndex: 'status',
            key: 'status',
            searchable: true,
            title: 'Status',
            onFilter: (value, record) => record.status.indexOf(value) === 0,
            filteredValue: filteredInfo?.status || null,
            render: (status, rowData) => <AntdTag status={status} />,
            filters:
              statusFilters || Object.keys(Statuses).map((s) => ({ value: s, text: Statuses[s] })),
          },
          {
            dataIndex: 'charge_code',
            key: 'chargeCode',
            ellipsis: true,
            searchable: false,
            title: 'Charge code',
            render: (charge_code) => charge_code.code,
          },
          {
            dataIndex: 'yuhu_modules',
            key: 'yuhuModules',
            title: 'Modules',
            ellipsis: true,
            render: (modules) => modules.join(', '),
          },
          {
            key: 'actions',
            title: 'Quick actions',
            searchable: false,
            render: (text, rowData) => renderRowActions(rowData),
          },
        ]}
      />
    </Card>
  );
};

FeesTable.propTypes = {
  onEditClick: PropTypes.func.isRequired,
  onResetClick: PropTypes.func,

  renderRowActions: PropTypes.func.isRequired,
  statusFilters: PropTypes.array.isRequired,

  // Run this function against all fee_templates in context
  // to decide if they belong on this table.
  feeTemplateIncluded: PropTypes.func,
  isMobile: PropTypes.bool,
};

FeesTable.defaultProps = {
  statusFilters: null,
};

export default FeesTable;
